import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Calendar, Plus, ThumbsUp, ThumbsDown, HelpCircle, Check, X, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export function DateProposals({ meetingId = null, onDateSelected }) {
  const { api, member, isChair } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProposal, setNewProposal] = useState({
    proposed_date: '',
    proposed_time: '',
    notes: ''
  });

  const fetchProposals = useCallback(async () => {
    try {
      const response = await api().get('/date-proposals');
      // Filter by meeting if meetingId provided
      let data = response.data;
      if (meetingId) {
        data = data.filter(p => p.meeting_id === meetingId);
      }
      setProposals(data);
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
    } finally {
      setLoading(false);
    }
  }, [api, meetingId]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const handleCreateProposal = async () => {
    if (!newProposal.proposed_date) {
      toast.error('Please select a date');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...newProposal,
        meeting_id: meetingId || null
      };
      await api().post('/date-proposals', payload);
      setCreateDialogOpen(false);
      setNewProposal({ proposed_date: '', proposed_time: '', notes: '' });
      toast.success('Date proposed!');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setCreating(false);
    }
  };

  const handleVote = async (proposalId, vote) => {
    try {
      await api().post(`/date-proposals/${proposalId}/vote`, { vote });
      toast.success('Vote recorded!');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    }
  };

  const handleSelectDate = async (proposalId) => {
    try {
      await api().put(`/date-proposals/${proposalId}/select`);
      toast.success('Date selected!');
      fetchProposals();
      onDateSelected?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select date');
    }
  };

  const handleDelete = async (proposalId) => {
    try {
      await api().delete(`/date-proposals/${proposalId}`);
      toast.success('Proposal deleted');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getUserVote = (proposal) => {
    const vote = proposal.votes?.details?.find(v => v.member_id === member?.id);
    return vote?.vote;
  };

  const getVoteButtonVariant = (proposal, voteType) => {
    const currentVote = getUserVote(proposal);
    if (currentVote === voteType) {
      return voteType === 'yes' ? 'default' : voteType === 'no' ? 'destructive' : 'secondary';
    }
    return 'outline';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const openProposals = proposals.filter(p => p.status === 'open');

  return (
    <div className="space-y-4" data-testid="date-proposals">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Date Proposals</h3>
          <p className="text-sm text-muted-foreground">
            Propose dates and vote on when to meet
          </p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="propose-date-btn">
              <Plus className="h-4 w-4" />
              Propose Date
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Propose a Meeting Date</DialogTitle>
              <DialogDescription>
                Suggest a date for the next forum meeting. Other members can vote on proposals.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={newProposal.proposed_date}
                  onChange={(e) => setNewProposal({ ...newProposal, proposed_date: e.target.value })}
                  data-testid="proposal-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time (optional)</Label>
                <Input
                  id="time"
                  type="time"
                  value={newProposal.proposed_time}
                  onChange={(e) => setNewProposal({ ...newProposal, proposed_time: e.target.value })}
                  data-testid="proposal-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional context..."
                  value={newProposal.notes}
                  onChange={(e) => setNewProposal({ ...newProposal, notes: e.target.value })}
                  data-testid="proposal-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateProposal} disabled={creating} data-testid="submit-proposal-btn">
                {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Propose Date
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {openProposals.length === 0 ? (
        <Card className="p-8">
          <div className="text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h4 className="font-medium mb-2">No date proposals yet</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Be the first to propose a date for the next meeting
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Propose Date
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {openProposals.map((proposal) => (
            <Card key={proposal.id} className="relative" data-testid={`proposal-${proposal.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">
                      {format(parseISO(proposal.proposed_date), 'EEEE, MMMM d')}
                    </CardTitle>
                    {proposal.proposed_time && (
                      <CardDescription className="mt-1">
                        at {proposal.proposed_time}
                      </CardDescription>
                    )}
                  </div>
                  <Badge variant="outline" className="gap-1">
                    <Users className="h-3 w-3" />
                    {proposal.votes?.total || 0} votes
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {proposal.notes && (
                  <p className="text-sm text-muted-foreground">{proposal.notes}</p>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Proposed by {proposal.proposer_name}
                </p>
                
                {/* Vote counts */}
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <ThumbsUp className="h-4 w-4" />
                    {proposal.votes?.yes || 0}
                  </span>
                  <span className="flex items-center gap-1 text-red-600">
                    <ThumbsDown className="h-4 w-4" />
                    {proposal.votes?.no || 0}
                  </span>
                  <span className="flex items-center gap-1 text-yellow-600">
                    <HelpCircle className="h-4 w-4" />
                    {proposal.votes?.maybe || 0}
                  </span>
                </div>
                
                {/* Vote buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={getVoteButtonVariant(proposal, 'yes')}
                    onClick={() => handleVote(proposal.id, 'yes')}
                    className="flex-1 gap-1"
                    data-testid={`vote-yes-${proposal.id}`}
                  >
                    <ThumbsUp className="h-4 w-4" />
                    Yes
                  </Button>
                  <Button
                    size="sm"
                    variant={getVoteButtonVariant(proposal, 'maybe')}
                    onClick={() => handleVote(proposal.id, 'maybe')}
                    className="flex-1 gap-1"
                    data-testid={`vote-maybe-${proposal.id}`}
                  >
                    <HelpCircle className="h-4 w-4" />
                    Maybe
                  </Button>
                  <Button
                    size="sm"
                    variant={getVoteButtonVariant(proposal, 'no')}
                    onClick={() => handleVote(proposal.id, 'no')}
                    className="flex-1 gap-1"
                    data-testid={`vote-no-${proposal.id}`}
                  >
                    <ThumbsDown className="h-4 w-4" />
                    No
                  </Button>
                </div>
                
                {/* Chair actions */}
                {isChair && (
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      onClick={() => handleSelectDate(proposal.id)}
                      className="flex-1 gap-1"
                      data-testid={`select-date-${proposal.id}`}
                    >
                      <Check className="h-4 w-4" />
                      Select This Date
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(proposal.id)}
                      className="text-destructive"
                      data-testid={`delete-proposal-${proposal.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
